import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Ruler, ArrowsOutCardinal, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut,
  Rectangle, ArrowUpRight, TextT, Trash, CircleNotch, Circle, Cloud,
  ArrowsHorizontal, Scribble, ScribbleLoop, Highlighter, Polygon, TextAa, CaretDown, LineSegment,
  LinkSimple, LinkBreak, Camera, StackSimple, User, UsersThree, EyeSlash, Intersect,
  ShareNetwork, PushPin, PushPinSlash, MagicWand, Hand,
} from '@phosphor-icons/react';
import type { Chinche, Foto, Hoja, Marca, MarcaData, Vinculo } from '@/api/laminas';
import { apiLaminas, parsearMarca } from '@/api/laminas';
import { BarraVertical } from './BarraVertical';
import type { BotonBarra } from './BarraVertical';
import { CapaMarcas } from './CapaMarcas';
import { CapaChinches, CapaFotos, CapaVinculos, DialogoChinche, DialogoVinculo } from './Chinchetas';
import { EditorFoto } from './EditorFoto';
import { CapaTiles, useTiles } from './CapaTiles';
import type { Vista } from './CapaTiles';
import { CAPAS_TODAS, PanelCapas, hayCapasOcultas } from './PanelCapas';
import type { Capas } from './PanelCapas';
import { COLOR_A, COLOR_B, PanelComparar } from './PanelComparar';
import type { Desfase, ModoComparar } from './PanelComparar';
import { PanelCompartir } from './PanelCompartir';
import {
  componerInstantanea, descargar, imprimirHoja, nombreDeArchivo, tilesCopiables, urlDeVista,
} from './compartir';
import type { VistaCompartida } from './compartir';
import {
  COLOR_MEDIDA, DE_ARRASTRE, DE_TEXTO, DE_TRAZO, DE_VERTICES, GROSORES, IMAN_ORTO, PASO_MEDIDA_PX,
  PALETA_COLORES, SIN_ESTILO, adelgazarTrazo, esMedida, featureDeArrastre,
  featureDeTexto, featureDeTrazo, featureDeVertices, imantarPunto,
  minimoVertices, puntosNube, puntosOvalo, reestilar, trasladar,
} from './marcado';
import type { Herramienta, Iman } from './marcado';

// El visor. Replica el mecanismo que hace rápido a Fieldwire, que NO es un
// visor listo: son tiles JPEG pre-renderizadas en el servidor y un <img> por
// tile visible. El navegador no abre el PDF nunca.
//
// Medido en su app al abrir una lámina: 76 tiles, 30,9 KB de peso medio y
// 145 ms de latencia media. La pirámide es CORTA — solo z0 (detalle) y z2
// (vista completa); el nivel intermedio lo escala el navegador. Por eso
// `nivelesTiles` es una lista ('0,2') y no un rango.
//
// Todo vive en el espacio de píxeles de la hoja: la capa interior mide
// `anchoPx × altoPx` y el pan/zoom es UN `transform` CSS sobre ella. Así las
// marcas (que vienen en píxeles crudos) se pintan sin convertir nada, y mover
// el plano no recalcula ninguna coordenada.

interface Props {
  hoja: Hoja;
  /**
   * Todas las hojas de esta lámina. Es el grupo «versiones» del selector de
   * comparación (F-6); la hoja base ya viene aparte en `hoja`.
   */
  hojasVersion?: Hoja[];
  marcas: Marca[];
  chinches: Chinche[];
  /** Los saltos a otras láminas pinchados sobre esta hoja (F-3). */
  vinculos?: Vinculo[];
  /** Las fotos de obra clavadas a este plano (F-4). */
  fotos?: Foto[];
  /** Qué lámina es esta: el diálogo de vínculo la excluye de los destinos. */
  idPlano?: number;
  /** Cómo se llama, para el nombre del archivo y el título de la impresión. */
  nombreLamina?: string;
  /**
   * F-7: el encuadre y las capas que traía un enlace compartido. Se aplica UNA
   * vez por hoja, en lugar de encajar. Tiene que venir MEMOIZADO: es un objeto
   * y entra en las dependencias del efecto que lo aplica.
   */
  vistaInicial?: VistaCompartida | null;
  /**
   * Pasa TODAS las marcas personales de esta hoja a la capa del equipo. Sin
   * esto la sección «Publicar» del panel no aparece.
   */
  onPublicarPersonales?: (ids: number[]) => void;
  /** Hay publicaciones en vuelo: el botón muestra el spinner. */
  publicando?: boolean;
  /**
   * Se llama al terminar de dibujar. El Feature ya viene en píxeles de la hoja.
   * `privada` es la capa elegida en la barra: personal (solo yo) o publicada.
   */
  onCrear?: (d: MarcaData, privada: boolean) => void;
  onBorrar?: (idMarca: number) => void;
  /**
   * Reescribe una marca existente: moverla, repintarla, cambiarle el texto o
   * pasarla de capa. `privada` viaja SIEMPRE y no es opcional a propósito: el
   * endpoint reescribe la marca entera, así que omitirlo la publicaba en
   * silencio al arrastrarla (F-2 llamaba a la mutación sin el flag y el cliente
   * lo completaba con `false`).
   */
  onActualizar?: (idMarca: number, d: MarcaData, privada: boolean) => void;
  /** Fija el puente px↔m de la hoja. `metros = null` la descalibra. */
  onCalibrar?: (distanciaPx: number, metros: number | null) => void;
  /** Saltar a otra lámina del juego. Sin esto los vínculos se ven pero no abren. */
  onIrALamina?: (idPlano: number) => void;
  onCrearVinculo?: (v: { posX: number; posY: number; planoDestinoId: number; descripcion: string }) => void;
  onBorrarVinculo?: (idVinculo: number) => void;
  /**
   * F-8. Clava una tarea de ArquiFlow en el punto. Sin esto la herramienta del
   * chinche no aparece: el visor se usa también para ver, no solo para editar.
   */
  onCrearChinche?: (c: { posX: number; posY: number; tareaId: number; titulo: string }) => void;
  onBorrarChinche?: (idChinche: number) => void;
  /** Abre la tarea del chinche. Es el `selectTask` global del appStore. */
  onAbrirTarea?: (idTarea: number) => void;
  /** Clava una foto en el punto pinchado. Sin esto la herramienta no aparece. */
  onSubirFoto?: (punto: { posX: number; posY: number }, archivo: File) => void;
  onBorrarFoto?: (idFoto: number) => void;
  /**
   * F-10: guarda las anotaciones dibujadas SOBRE la foto. Sin esto el editor
   * abre y deja dibujar, pero no muestra el botón de guardar — que es la forma
   * honesta de decir «acá se ve, no se anota» cuando el visor está embebido en
   * un lugar de solo lectura.
   */
  onGuardarMarcasFoto?: (idFoto: number, marcas: MarcaData[]) => void;
  guardandoMarcasFoto?: boolean;
  /** Hay un POST/DELETE en vuelo: la barra lo muestra en vez de fingir instantáneo. */
  guardando?: boolean;
}

// La paleta y la geometría viven en `./marcado`. Acá quedan solo los gestos:
// arrastre de dos puntos, trazo libre, y toque con texto. `calibrar` no dibuja
// nada — reusa el arrastre para capturar una distancia de referencia y
// convertirla en el `metrosPorPixel` de la hoja.

// La paleta contextual de Procore: grupos que muestran la última herramienta
// usada y abren un submenú con sus hermanas. Es data, no código: agregar una
// herramienta acá y su caso en `marcado.ts` alcanza.
interface Opcion { h: Herramienta; nombre: string; icono: React.ReactNode }
interface Grupo { clave: string; opciones: Opcion[] }

const ico = (C: typeof Rectangle) => <C size={16} weight="bold" />;

/**
 * Diámetro de la lupa, en píxeles de pantalla. Dos tallas porque 132 px es una
 * cosa en una compu y otra en un celular: medido a 390 px se comía el 36 % del
 * ancho del visor y tapaba 4 de los 6 botones de la fila de ver —en compu solo
 * tapaba 2—. A 104 px tapa dos y sigue mostrando 35 px de hoja a ×3, que es
 * suficiente para apuntar a una esquina.
 */
const LUPA_PX = 132;
const LUPA_PX_ANGOSTA = 104;
/** Desde dónde el visor deja de ser un celular. Es el `sm` de Tailwind. */
const VISOR_ANGOSTO = 640;
/**
 * Cuánto amplía la lupa respecto a lo que ya se ve. Es relativo y no absoluto a
 * propósito: lo que hace falta es ver TRES VECES MEJOR que ahora, y eso vale
 * tanto encajado como acercado.
 */
const LUPA_ZOOM = 3;

const GRUPOS: Grupo[] = [
  { clave: 'medir', opciones: [
    { h: 'medir', nombre: 'Medir distancia', icono: ico(Ruler) },
    { h: 'area', nombre: 'Medir área', icono: ico(Polygon) },
    // La tercera hermana del abanico. No compite con las otras dos: ellas
    // miden lo RECTO con exactitud de vértice, y esto mide lo que no tiene
    // esquinas —un lindero curvo, una acera, el borde de una terraza— donde
    // clavar punto por punto sería absurdo.
    { h: 'medirLibre', nombre: 'Medir a mano alzada', icono: ico(ScribbleLoop) },
  ] },
  { clave: 'figura', opciones: [
    { h: 'rect', nombre: 'Cuadrado', icono: ico(Rectangle) },
    { h: 'oval', nombre: 'Óvalo', icono: ico(Circle) },
    { h: 'nube', nombre: 'Nube de revisión', icono: ico(Cloud) },
  ] },
  { clave: 'linea', opciones: [
    { h: 'flecha', nombre: 'Flecha', icono: ico(ArrowUpRight) },
    { h: 'linea', nombre: 'Línea', icono: ico(LineSegment) },
    { h: 'flecha2', nombre: 'Doble flecha', icono: ico(ArrowsHorizontal) },
  ] },
  { clave: 'libre', opciones: [
    { h: 'libre', nombre: 'Mano alzada', icono: ico(Scribble) },
    { h: 'marcador', nombre: 'Marcador resaltador', icono: ico(Highlighter) },
  ] },
  { clave: 'texto', opciones: [
    { h: 'texto', nombre: 'Nota de texto', icono: ico(TextT) },
    { h: 'textoCaja', nombre: 'Texto con recuadro', icono: ico(TextAa) },
  ] },
  // F-3. No dibuja nada: pincha un salto a otra lámina. Va en la misma barra
  // porque es el mismo acto («marcar algo en este punto del plano»), aunque lo
  // que queda no sea una marca.
  { clave: 'vinculo', opciones: [
    { h: 'vinculo', nombre: 'Vincular a otra lámina', icono: ico(LinkSimple) },
    { h: 'desvincular', nombre: 'Quitar un vínculo', icono: ico(LinkBreak) },
  ] },
  // F-4. Grupo de una sola opción: no hay «despegar foto» porque para quitarla
  // hay que verla primero, y ese botón vive en el visor de la foto.
  { clave: 'foto', opciones: [
    { h: 'foto', nombre: 'Clavar una foto', icono: ico(Camera) },
  ] },
  // F-8. El chinche sí tiene su «despinchar» —al contrario de la foto— porque
  // se ve de una: la pastilla con el número de tarea está a la vista en el
  // plano, no hay que abrir nada para encontrarla.
  { clave: 'chinche', opciones: [
    { h: 'chinche', nombre: 'Clavar una tarea', icono: ico(PushPin) },
    { h: 'despinchar', nombre: 'Despinchar una tarea', icono: ico(PushPinSlash) },
  ] },
];

export function VisorLamina({ hoja, hojasVersion = [], marcas, chinches, vinculos = [], fotos = [], idPlano,
                              nombreLamina = 'lamina', vistaInicial = null,
                              onCrear, onBorrar, onActualizar, onCalibrar,
                              onIrALamina, onCrearVinculo, onBorrarVinculo,
                              onCrearChinche, onBorrarChinche, onAbrirTarea,
                              onSubirFoto, onBorrarFoto, onGuardarMarcasFoto,
                              guardandoMarcasFoto, onPublicarPersonales,
                              publicando, guardando }: Props) {
  /** El marco del visor. Ancla los desplegables de la barra a SU caja. */
  const raiz = useRef<HTMLDivElement | null>(null);
  const caja = useRef<HTMLDivElement | null>(null);
  const [vista, setVista] = useState<Vista>({ escala: 0.2, x: 0, y: 0 });
  const [herramienta, setHerramienta] = useState<Herramienta>('mover');
  const [marcaSel, setMarcaSel] = useState<number | null>(null);
  const [color, setColor] = useState(PALETA_COLORES[0]);
  const [grosor, setGrosor] = useState(GROSORES[1].valor);
  /** Qué herramienta quedó activa en cada grupo de la paleta. */
  const [elegidaPorGrupo, setElegidaPorGrupo] = useState<Record<string, Herramienta>>(
    () => Object.fromEntries(GRUPOS.map(g => [g.clave, g.opciones[0].h])),
  );
  /**
   * Qué grupo tiene el submenú abierto y a qué altura quedó su botón. El `top`
   * se mide del DOM al abrir en vez de calcularlo con la altura de fila: la
   * barra es de otro archivo y un cambio de tamaño allá dejaría el menú
   * flotando lejos del botón sin que nada falle.
   */
  const [submenu, setSubmenu] = useState<{ clave: string; top: number } | null>(null);
  /** El desplegable de color, grosor y capa — un botón en la barra, no ocho. */
  const [estilos, setEstilos] = useState(false);
  /** F-5: qué capas se están viendo. Es filtro de pantalla, no de datos. */
  const [capas, setCapas] = useState<Capas>(vistaInicial?.capas ?? CAPAS_TODAS);
  const [panelCapas, setPanelCapas] = useState(false);
  /** F-7: compartir. `corsTiles === null` = todavía no se midió. */
  const [panelCompartir, setPanelCompartir] = useState(false);
  const [corsTiles, setCorsTiles] = useState<boolean | null>(null);
  /** F-6: contra qué hoja se compara, y cómo. */
  const [panelComparar, setPanelComparar] = useState(false);
  const [idHojaB, setIdHojaB] = useState<number | null>(null);
  const [modoComparar, setModoComparar] = useState<ModoComparar>('super');
  const [cualHoja, setCualHoja] = useState<'a' | 'b'>('a');
  const [desfase, setDesfase] = useState<Desfase>({ x: 0, y: 0 });
  /** En qué capa va a caer la PRÓXIMA marca que se dibuje. */
  const [privadaNueva, setPrivadaNueva] = useState(false);
  /** El punto recién pinchado, esperando que se elija a qué lámina lleva. */
  const [vinculoNuevo, setVinculoNuevo] = useState<{ x: number; y: number } | null>(null);
  /** F-8: el punto recién pinchado, esperando qué tarea se le clava. */
  const [chincheNuevo, setChincheNuevo] = useState<{ x: number; y: number } | null>(null);
  /** Qué foto se está mirando en grande. */
  const [fotoVista, setFotoVista] = useState<Foto | null>(null);
  // El punto donde se va a clavar la foto, capturado al apoyar el dedo. Viaja en
  // un ref porque entre el toque y el `change` del input no hay render de por
  // medio.
  const puntoFoto = useRef<{ posX: number; posY: number } | null>(null);
  // EL GESTO EN CURSO VIVE EN UN REF, no en el estado, y esto no es un detalle:
  // con el estado, `pointermove` leía el valor del render anterior y entre un
  // `down` y un `up` rápidos React no alcanza a refrescar — el trazo llegaba a
  // `pointerup` con UN punto y la marca no se creaba nunca, sin ningún error.
  // Medido el 20-oct-2026: la mano alzada fallaba siempre y la línea recta de
  // forma intermitente. El ref es la verdad; el estado es solo la copia que se
  // pinta.
  //
  // Las de arrastre guardan [inicio, fin] y las de trazo libre todo el
  // recorrido: el mismo arreglo cubre los dos gestos.
  const gesto = useRef<[number, number][] | null>(null);
  const [gestoVista, setGestoVista] = useState<[number, number][] | null>(null);
  const ponerGesto = (pts: [number, number][] | null) => {
    gesto.current = pts;
    setGestoVista(pts);
  };
  // F-9. Los vértices ya CLAVADOS del trazo de medición. Es lo único del visor
  // que sobrevive entre gestos: `gesto` nace y muere en un toque, y esto dura
  // desde el primer vértice hasta el doble toque que cierra. Por eso no podía
  // vivir adentro de `gesto` reusándolo — se perdía en cada `pointerup`.
  const vertices = useRef<[number, number][]>([]);
  const [verticesVista, setVerticesVista] = useState<[number, number][]>([]);
  const ponerVertices = (pts: [number, number][]) => {
    vertices.current = pts;
    setVerticesVista(pts);
  };
  // Lo que el imán agarró en este cuadro, para pintarle la guía punteada. Sin
  // dibujarla el imán es invisible: el punto «salta» y se lee como un error.
  const [iman, setIman] = useState<Iman>({ x: null, y: null });
  // Arrastrar una marca ya creada. Mismo patrón que el gesto de dibujo y por la
  // misma razón: el ref es la verdad (lo lee `pointermove`) y el estado es la
  // copia que se pinta.
  const movida = useRef<{ id: number; x: number; y: number; dx: number; dy: number } | null>(null);
  const [movidaVista, setMovidaVista] = useState<{ id: number; dx: number; dy: number } | null>(null);
  const ponerMovida = (m: typeof movida.current) => {
    movida.current = m;
    setMovidaVista(m && { id: m.id, dx: m.dx, dy: m.dy });
  };
  const arrastre = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  // Punteros activos. Con DOS dedos el gesto deja de ser pan y pasa a pellizco.
  // Sin esto el celular no tiene ninguna forma de acercar: no hay rueda de mouse
  // y `touch-none` (necesario para dibujar) apaga el pinch del navegador.
  const punteros = useRef(new Map<number, { x: number; y: number }>());
  const pellizco = useRef<{ dist: number; cx: number; cy: number } | null>(null);
  /** Último toque, para detectar el doble-tap que acerca. */
  const ultimoTap = useRef<{ t: number; x: number; y: number } | null>(null);
  const [tam, setTam] = useState({ w: 0, h: 0 });

  // Medir el contenedor para saber qué tiles entran en pantalla. Sin esto hay
  // que pedirlas todas, que es justamente lo que el teselado evita.
  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setTam({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    setTam({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const encajar = useCallback(() => {
    if (!tam.w || !tam.h || !hoja.anchoPx || !hoja.altoPx) return;
    const escala = Math.min(tam.w / hoja.anchoPx, tam.h / hoja.altoPx) * 0.95;
    setVista({
      escala,
      x: (tam.w - hoja.anchoPx * escala) / 2,
      y: (tam.h - hoja.altoPx * escala) / 2,
    });
  }, [tam.w, tam.h, hoja.anchoPx, hoja.altoPx]);

  // F-7: la hoja cuyo encuadre ya se restauró desde un enlace compartido.
  // Importa que sea UNA sola vez: `encajar` se rehace cuando cambia el tamaño
  // del contenedor, así que sin esta marca el primer resize (o el propio
  // ResizeObserver al montar) pisaba el encuadre del enlace con el encaje.
  const encuadreRestaurado = useRef<number | null>(null);

  // Encajar al cambiar de hoja o al conocer el tamaño del contenedor, salvo que
  // el enlace haya traído un encuadre propio para ESTA hoja.
  useEffect(() => {
    if (!tam.w || !tam.h) return;
    const traeEncuadre = !!vistaInicial && vistaInicial.z > 0
      && (vistaInicial.idHoja === null || vistaInicial.idHoja === hoja.id);
    if (traeEncuadre && encuadreRestaurado.current !== hoja.id) {
      encuadreRestaurado.current = hoja.id;
      // El enlace guarda el PUNTO DE LA HOJA que quedó al centro, no el x/y de
      // pantalla: así el encuadre se reconstruye igual en otra resolución.
      const z = vistaInicial!.z;
      setVista({
        escala: z,
        x: tam.w / 2 - vistaInicial!.cx * z,
        y: tam.h / 2 - vistaInicial!.cy * z,
      });
      return;
    }
    encajar();
  }, [encajar, hoja.id, tam.w, tam.h, vistaInicial]);

  const { nivel, visibles } = useTiles(hoja, vista, tam);

  // ── Comparación (F-6) ─────────────────────────────────────────────────────

  // La hoja comparada se pide por el MISMO endpoint y con la MISMA clave que la
  // base: saltar entre comparadas no vuelve a pegarle al servidor, y comparar
  // contra la versión que ya se estaba mirando sale de la caché.
  const { data: detalleB, isLoading: cargandoB } = useQuery({
    queryKey: ['laminas', 'hoja', idHojaB],
    queryFn: () => apiLaminas.hoja(idHojaB!),
    enabled: idHojaB !== null,
  });
  const hojaB = detalleB?.hoja ?? null;
  const comparando = idHojaB !== null && !!hojaB;

  // Si los papeles no miden lo mismo, la comparada se escala por el ancho: son
  // el mismo plano rasterizado a otro dpi, no dos dibujos distintos. El panel
  // avisa cuando además cambia la proporción.
  const escalaB = hojaB && hojaB.anchoPx ? hoja.anchoPx / hojaB.anchoPx : 1;

  // La vista de la hoja B: la misma cámara, corrida por el desfase y con su
  // propia escala. Así `useTiles` recorta su viewport sin saber nada de la
  // comparación. El desfase está en píxeles de la hoja A, que es el espacio en
  // el que vive todo lo demás del visor.
  const vistaB: Vista = {
    escala: vista.escala * escalaB,
    x: vista.x + desfase.x * vista.escala,
    y: vista.y + desfase.y * vista.escala,
  };
  const tilesB = useTiles(comparando ? hojaB : null, vistaB, tam);

  // Un toque del D-pad mueve ~6 px de PANTALLA, no de hoja: a la escala de
  // «encajar» (0,02) un paso fijo de 10 px de hoja no se vería mover, y al 400 %
  // saltaría media pared.
  const pasoDpad = Math.max(1, 6 / vista.escala);
  const moverB = (dx: number, dy: number) =>
    setDesfase(d => ({ x: d.x + dx, y: d.y + dy }));

  const dejarDeComparar = () => {
    setIdHojaB(null);
    setDesfase({ x: 0, y: 0 });
    setModoComparar('super');
    setCualHoja('a');
  };

  const elegirComparada = (id: number | null) => {
    setIdHojaB(id);
    // El desfase es de un PAR de hojas: arrastrarlo a la siguiente comparada
    // dejaría el plano corrido sin que nadie lo haya pedido.
    setDesfase({ x: 0, y: 0 });
    setCualHoja('a');
  };

  // ── Capas (F-5) ───────────────────────────────────────────────────────────

  // Clasificar una vez por cambio de lista. La medida no se distingue por una
  // tabla aparte sino por sus `properties`, así que hay que parsear el GeoJSON
  // para saberlo; memorizarlo acá es lo que evita rehacerlo en cada cuadro de
  // un arrastre.
  const clasificadas = useMemo(
    () => marcas.map(m => {
      const d = parsearMarca(m);
      return { m, medida: !!d && esMedida(d) };
    }),
    [marcas],
  );

  // Los conteos se calculan sobre TODO lo que llegó, no sobre lo visible: el
  // panel tiene que poder decir «Medidas 7» mientras las medidas están
  // apagadas, o apagar una capa dejaría su propio contador en cero y no habría
  // forma de saber qué se está escondiendo.
  const conteos = useMemo(() => ({
    marcas: clasificadas.filter(x => !x.medida).length,
    medidas: clasificadas.filter(x => x.medida).length,
    fotos: fotos.length,
    chinches: chinches.length,
    vinculos: vinculos.length,
    // Publicada / personal solo aplica a marcas: fotos, chinches y vínculos no
    // tienen dueño en el modelo.
    publicadas: marcas.filter(m => !m.privada).length,
    // Las únicas privadas que llegan son las propias: el servidor ya descarta
    // las ajenas (`!m.Privada || m.IdColCreador == idCol`). Por eso la fila
    // dice «mías» y no hace falta saber de quién es cada una.
    personales: marcas.filter(m => m.privada).length,
  }), [clasificadas, marcas, fotos.length, chinches.length, vinculos.length]);

  const marcasVisibles = useMemo(
    () => clasificadas
      .filter(x => (x.medida ? capas.medidas : capas.marcas))
      .filter(x => (x.m.privada ? capas.personales : capas.publicadas))
      .map(x => x.m),
    [clasificadas, capas],
  );

  // Una marca escondida no puede quedar seleccionada: la barra seguiría
  // ofreciendo repintarla y borrarla sin que se vea cuál es.
  useEffect(() => {
    if (marcaSel !== null && !marcasVisibles.some(m => m.id === marcaSel)) setMarcaSel(null);
  }, [marcaSel, marcasVisibles]);

  const cambiarCapa = (clave: keyof Capas, valor: boolean) =>
    setCapas(c => ({ ...c, [clave]: valor }));

  // ── Compartir (F-7) ───────────────────────────────────────────────────────

  // El enlace lleva el punto de la HOJA que quedó al centro de la pantalla.
  // Invertir el transform acá es lo que lo vuelve independiente del tamaño de
  // la ventana de quien lo armó.
  const urlCompartir = useMemo(() => {
    if (idPlano === undefined || !tam.w || !tam.h || !vista.escala) return '';
    return urlDeVista({
      idPlano,
      idHoja: hoja.id,
      cx: (tam.w / 2 - vista.x) / vista.escala,
      cy: (tam.h / 2 - vista.y) / vista.escala,
      z: vista.escala,
      capas,
    });
  }, [idPlano, hoja.id, tam.w, tam.h, vista.x, vista.y, vista.escala, capas]);

  /**
   * El SVG de marcas que YA está pintado. La instantánea y la impresión lo
   * clonan en vez de volver a recorrer el GeoJSON: el pintor es `CapaMarcas` y
   * tener un segundo pintor para exportar es la forma más segura de que la
   * foto deje de parecerse a la pantalla.
   */
  const svgDeMarcas = () =>
    (caja.current?.querySelector('[data-capa-marcas]') as SVGSVGElement | null) ?? null;

  const pedidoInstantanea = () => ({ hoja, vista, tam, visibles, svgMarcas: svgDeMarcas() });

  const descargarPng = async () => {
    const blob = await componerInstantanea(pedidoInstantanea());
    descargar(blob, nombreDeArchivo(hoja, nombreLamina, 'png'));
  };

  const copiarPng = async () => {
    const blob = await componerInstantanea(pedidoInstantanea());
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  };

  const imprimir = () => imprimirHoja({ hoja, titulo: nombreLamina, svgMarcas: svgDeMarcas() });

  // Se mide al abrir el panel, no al montar el visor: es una request de más por
  // cada lámina que se abre, y la gran mayoría de las veces nadie va a exportar
  // nada. Se mide UNA vez por visor (`corsTiles !== null` corta).
  useEffect(() => {
    if (!panelCompartir || corsTiles !== null) return;
    const url = visibles[0]?.url;
    if (!url) return;
    let vivo = true;
    tilesCopiables(url).then(r => { if (vivo) setCorsTiles(r); });
    return () => { vivo = false; };
  }, [panelCompartir, corsTiles, visibles]);

  // F-9. Un trazo de medición a medio clavar muere al cambiar de herramienta o
  // de hoja. Sin esto, elegir «Área», poner dos vértices, irse a la lámina
  // siguiente y volver a medir ahí arrancaría con dos puntos fantasma de OTRO
  // plano — y el doble toque los cerraría en una figura que nadie dibujó.
  useEffect(() => {
    vertices.current = [];
    setVerticesVista([]);
    setIman({ x: null, y: null });
  }, [herramienta, hoja.id]);

  /** Las marcas personales de esta hoja: son las que «Publicar» pasa al equipo. */
  const idsPersonales = useMemo(
    () => marcas.filter(m => m.privada).map(m => m.id),
    [marcas],
  );

  /** En qué capa está hoy una marca ya guardada. */
  const privadaDe = (id: number) => marcas.find(m => m.id === id)?.privada ?? false;

  /**
   * Todo lo que crea o reescribe una marca pasa por estos dos y no llama a las
   * props directo: así la capa es UNA decisión tomada en un solo lugar, y mover
   * o repintar no puede olvidarse de arrastrar el flag.
   */
  const crearMarca = (d: MarcaData) => {
    // Dibujar en la capa personal con esa capa apagada haría desaparecer la
    // marca en el mismo gesto que la creó. Se prende sola.
    if (privadaNueva) cambiarCapa('personales', true);
    onCrear?.(d, privadaNueva);
  };
  const reescribirMarca = (id: number, d: MarcaData) => onActualizar?.(id, d, privadaDe(id));

  // ── Pan, zoom y medición ──────────────────────────────────────────────────

  /**
   * Cuánto se levanta el punto de trabajo POR ENCIMA del dedo, en píxeles de
   * PANTALLA. Copiado del `ALZADO_PX` del editor de /planta, donde Daniel lo
   * pidió el 4-oct: sin él la yema tapa justo el vértice que uno está tratando
   * de poner, y clavarlo con precisión es imposible.
   *
   * En píxeles de pantalla y no en metros a propósito: se resta antes de pasar
   * por la escala, así el alzado se ve igual a cualquier zoom. Medirlo en
   * metros sería el mismo defecto con otro nombre — crecería al acercar.
   *
   * Solo aplica al DEDO: el mouse no tapa nada y correrle el cursor al usuario
   * sería mentirle.
   */
  const ALZADO_PX = 44;

  /** De coordenadas de pantalla a píxeles de la hoja. */
  const aHoja = (ev: React.PointerEvent, alzar = false): [number, number] => {
    const r = caja.current!.getBoundingClientRect();
    const alzado = alzar && ev.pointerType !== 'mouse' ? ALZADO_PX : 0;
    return [
      (ev.clientX - r.left - vista.x) / vista.escala,
      (ev.clientY - r.top - alzado - vista.y) / vista.escala,
    ];
  };

  /**
   * El punto de un vértice en curso, ya imantado contra sus vecinos.
   *
   * El umbral se pide en METROS (`IMAN_ORTO`) y se traduce a píxeles de hoja
   * con la calibración. Sin calibrar no hay metros, y ahí se cae a los 14 px de
   * PANTALLA del `IMAN_PX` de /planta: el imán tiene que seguir ayudando en una
   * hoja recién subida, que es justo cuando uno está midiendo para calibrarla.
   */
  const puntoImantado = (ev: React.PointerEvent): [number, number] => {
    const p = aHoja(ev, true);
    const vs = vertices.current;
    if (!vs.length) { setIman({ x: null, y: null }); return p; }
    const umbral = hoja.metrosPorPixel
      ? IMAN_ORTO / hoja.metrosPorPixel
      : 14 / vista.escala;
    // Los dos vecinos de un punto que todavía no existe: el último clavado y el
    // primero, que es contra el que se va a cerrar.
    const { p: q, iman: i } = imantarPunto([vs[vs.length - 1], vs[0]], p[0], p[1], umbral);
    setIman(i);
    return q;
  };

  /** Deja el trazo de medición en blanco. Se llama al cerrar y al cambiar de modo. */
  const soltarVertices = () => {
    ponerVertices([]);
    setIman({ x: null, y: null });
  };

  /**
   * Cierra el trazo de medición y lo guarda. Devuelve si cerró algo: el doble
   * toque lo usa para decidir si ya atendió el gesto o si tiene que seguir
   * siendo el zoom de siempre.
   */
  const cerrarVertices = (): boolean => {
    const vs = vertices.current;
    if (vs.length < minimoVertices(herramienta)) return false;
    const f = featureDeVertices(herramienta, vs, color, grosor);
    soltarVertices();
    ponerGesto(null);
    if (f) crearMarca(f);
    return true;
  };

  /** Zoom anclado a un punto del contenedor: ese punto no se mueve de la pantalla. */
  const zoomEnPunto = (factor: number, cx: number, cy: number) => setVista(v => {
    const escala = Math.min(8, Math.max(0.02, v.escala * factor));
    const k = escala / v.escala;
    return { escala, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
  });

  /**
   * Bajar el dedo SOBRE una marca. Corre antes que `alBajar` del contenedor
   * (el evento nace acá y burbuja hacia afuera), así que `alBajar` ve la movida
   * ya sembrada y sabe que no tiene que arrancar el pan.
   *
   * Selecciona y prepara el arrastre con el mismo gesto: si se suelta sin
   * moverse fue un clic de selección, y si se mueve, la marca se va con el dedo.
   */
  const agarrarMarca = (id: number, ev: React.PointerEvent) => {
    if (herramienta !== 'mover') return;
    setMarcaSel(id);
    if (!onActualizar) return;
    ponerMovida({ id, x: ev.clientX, y: ev.clientY, dx: 0, dy: 0 });
  };

  const alBajar = (ev: React.PointerEvent) => {
    const r = caja.current!.getBoundingClientRect();
    // Tocar el plano cierra lo que la barra tenga desplegado: no hace falta un
    // listener de clic-afuera porque el plano ocupa todo lo que no es la barra.
    setSubmenu(null);
    setEstilos(false);
    punteros.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    // La captura va al contenedor, no al <img> de la tile que recibió el toque:
    // al arrastrar, la tile de abajo cambia y el gesto perdería los eventos.
    ev.currentTarget.setPointerCapture?.(ev.pointerId);

    // Segundo dedo: el gesto se convierte en pellizco. Se aborta lo que hubiera
    // arrancado con el primero (pan o trazo) para no sembrar una marca al acercar.
    if (punteros.current.size === 2) {
      const [a, b] = [...punteros.current.values()];
      pellizco.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        cx: (a.x + b.x) / 2 - r.left,
        cy: (a.y + b.y) / 2 - r.top,
      };
      arrastre.current = null;
      ponerGesto(null);
      ponerMovida(null);
      return;
    }
    if (punteros.current.size > 2) return;

    // Doble toque = acercar donde se tocó. Es la forma cómoda de acercar con una
    // mano, y no compite con dibujar: dos toques sin arrastre no pasan el umbral
    // de 4 px que exige `alSubir`, así que nunca crean una marca.
    //
    // F-9 le mete UN inquilino más, y por eso el doble toque se resuelve acá y
    // no en dos lugares: midiendo con vértices ya puestos, el doble toque
    // CIERRA la figura en vez de acercar. Daniel lo eligió así para que el
    // gesto sea el mismo de Fieldwire (20-oct), sabiendo que se lo quita al
    // zoom. La pérdida es acotada a propósito: solo mientras hay un trazo de
    // medición abierto, que es el único momento en que acercar de golpe sería
    // además dañino —mueve el plano bajo los vértices que uno viene clavando—.
    // El pellizco sigue acercando siempre, también en medio del trazo.
    const prev = ultimoTap.current;
    ultimoTap.current = { t: ev.timeStamp, x: ev.clientX, y: ev.clientY };
    if (prev && ev.timeStamp - prev.t < 300
        && Math.hypot(ev.clientX - prev.x, ev.clientY - prev.y) < 30) {
      ultimoTap.current = null;
      ponerGesto(null);
      ponerMovida(null);
      // El primer toque del par YA clavó su vértice, y ese vértice es legítimo:
      // es la última esquina. El segundo solo dice «ya». Por eso no hay nada
      // que deshacer antes de cerrar.
      if (DE_VERTICES.includes(herramienta)) {
        if (cerrarVertices()) return;
        // No alcanzaron los vértices para cerrar nada, así que el doble toque
        // era un «acercame». Se barre el vértice suelto que dejó el primer
        // toque: si no, el zoom deja un punto huérfano en la hoja.
        soltarVertices();
      }
      zoomEnPunto(2, ev.clientX - r.left, ev.clientY - r.top);
      return;
    }

    // Tocar el plano fuera de toda marca la suelta. Si el toque cayó ENCIMA de
    // una, `agarrarMarca` ya corrió y dejó la movida sembrada, así que esto no
    // la deselecciona justo cuando se la acaba de elegir.
    if (herramienta === 'mover' && !movida.current) setMarcaSel(null);

    if (DE_TEXTO.includes(herramienta)) {
      const p = aHoja(ev);
      const texto = window.prompt('Nota sobre el plano');
      if (texto?.trim()) crearMarca(featureDeTexto(herramienta, p, texto.trim(), color));
      return;
    }
    if (herramienta === 'vinculo') {
      // El punto se guarda y el destino se pregunta después: el punto es el dato
      // difícil de capturar, y preguntar primero a qué lámina va obligaría a
      // recordar la respuesta mientras se busca dónde pinchar.
      const [x, y] = aHoja(ev);
      setVinculoNuevo({ x, y });
      return;
    }
    if (herramienta === 'chinche') {
      // Mismo orden que el vínculo y por el mismo motivo: el punto primero.
      const [x, y] = aHoja(ev);
      setChincheNuevo({ x, y });
      return;
    }
    if (DE_VERTICES.includes(herramienta)) {
      // El vértice nace PROVISIONAL y se clava al levantar el dedo. Ese hueco
      // entre apoyar y soltar es el que deja corregir la puntería sin borrar
      // nada: se apoya, se ve el punto alzado sobre la yema, se corre hasta
      // donde va, y recién ahí queda.
      ponerGesto([puntoImantado(ev)]);
      return;
    }
    if (DE_TRAZO.includes(herramienta) || DE_ARRASTRE.includes(herramienta)) {
      // UN punto, no dos. Sembrarlo duplicado parecía inofensivo (el arrastre
      // necesita un extremo) pero el adelgazado del trazo libre descarta el
      // repetido y un lazo corto se quedaba en dos vértices: el área nunca se
      // creaba. Un gesto que termina con un solo punto es un toque, y lo filtra
      // `alSubir`.
      //
      // La de mano alzada sí alza el punto sobre la yema, como las de vértices:
      // es medición, y el arranque de una medida es tan importante como su
      // final. El lápiz no lo hace a propósito —ahí el trazo tiene que salir de
      // donde está el dedo, o se dibuja corrido.
      ponerGesto([aHoja(ev, herramienta === 'medirLibre')]);
      return;
    }
    // Arrastrando una marca no se mueve el plano: el gesto ya tiene dueño.
    if (movida.current) return;
    arrastre.current = { x: ev.clientX, y: ev.clientY, vx: vista.x, vy: vista.y };
  };

  const alMover = (ev: React.PointerEvent) => {
    const activo = punteros.current.get(ev.pointerId);
    if (activo) { activo.x = ev.clientX; activo.y = ev.clientY; }

    if (pellizco.current && punteros.current.size >= 2) {
      const r = caja.current!.getBoundingClientRect();
      const [a, b] = [...punteros.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const cx = (a.x + b.x) / 2 - r.left;
      const cy = (a.y + b.y) / 2 - r.top;
      const ant = pellizco.current;
      pellizco.current = { dist, cx, cy };
      if (ant.dist < 1) return;
      setVista(v => {
        const escala = Math.min(8, Math.max(0.02, v.escala * (dist / ant.dist)));
        const k = escala / v.escala;
        // Se acerca anclado al punto medio Y se sigue su arrastre: el pellizco
        // mueve y acerca en el mismo gesto, como en cualquier visor de planos.
        return {
          escala,
          x: ant.cx - (ant.cx - v.x) * k + (cx - ant.cx),
          y: ant.cy - (ant.cy - v.y) * k + (cy - ant.cy),
        };
      });
      return;
    }

    // El corrimiento se mide en píxeles de HOJA: la pantalla se mueve en
    // píxeles de pantalla, y la marca vive en el plano.
    const m = movida.current;
    if (m) {
      ponerMovida({
        ...m,
        dx: (ev.clientX - m.x) / vista.escala,
        dy: (ev.clientY - m.y) / vista.escala,
      });
      return;
    }

    const g = gesto.current;
    if (g) {
      // Cuatro gestos, cuatro reglas: el de vértices corre su punto provisional
      // (imantado), el arrastre reemplaza el extremo, el trazo libre acumula, y
      // la medida a mano alzada acumula PERO a paso fijo.
      if (DE_VERTICES.includes(herramienta)) { ponerGesto([puntoImantado(ev)]); return; }
      if (herramienta === 'medirLibre') {
        // El último punto es PROVISIONAL: sigue al dedo y recién se clava
        // cuando se alejó `PASO_MEDIDA_PX` del anterior. El filtro va acá y no
        // al guardar por una razón que se ve: así el número que se lee mientras
        // se traza es EXACTAMENTE el que va a quedar guardado. Adelgazando al
        // soltar, el acumulado saltaría hacia abajo al levantar el dedo —y el
        // salto sería de hasta un 16 %, que es justo el error que se está
        // tratando de sacar (ver `PASO_MEDIDA_PX`).
        const p = aHoja(ev, true);
        const clavados = g.length > 1 ? g.slice(0, -1) : g;
        const u = clavados[clavados.length - 1];
        // Lo que se mide es si el PROVISIONAL ya se ganó su lugar, no si lo
        // hizo el punto nuevo. Medir el nuevo parece lo mismo y no lo es:
        // cuando él cruza el umbral, el que se clava es el provisional de
        // antes, que está justo por debajo —y los tramos salen de medio paso.
        const prov = g[g.length - 1];
        const lejos = g.length > 1
          && Math.hypot(prov[0] - u[0], prov[1] - u[1]) * vista.escala >= PASO_MEDIDA_PX;
        ponerGesto(lejos ? [...g, p] : [...clavados, p]);
        return;
      }
      ponerGesto(DE_TRAZO.includes(herramienta) ? [...g, aHoja(ev)] : [g[0], aHoja(ev)]);
      return;
    }
    const a = arrastre.current;
    if (!a) return;
    setVista(v => ({ ...v, x: a.vx + (ev.clientX - a.x), y: a.vy + (ev.clientY - a.y) }));
  };

  const alSubir = (ev: React.PointerEvent) => {
    punteros.current.delete(ev.pointerId);
    // Levantar un dedo del pellizco no reanuda el pan con el que queda: el gesto
    // termina cuando se sueltan los dos.
    if (punteros.current.size < 2) pellizco.current = null;
    arrastre.current = null;

    const m = movida.current;
    ponerMovida(null);
    if (m) {
      // Mismo umbral de 4 px de pantalla que para dibujar: por debajo, el gesto
      // fue un clic de selección y la marca no se toca. Sin esto, elegir una
      // marca mandaba un PUT con un corrimiento de medio píxel cada vez.
      if (Math.hypot(m.dx, m.dy) * vista.escala >= 4) {
        const d = parsearMarca(marcas.find(x => x.id === m.id)!);
        if (d) reescribirMarca(m.id, trasladar(d, m.dx, m.dy));
      }
      return;
    }

    const pts = gesto.current;
    ponerGesto(null);

    // F-9. Va ANTES de los dos filtros de abajo y no es un detalle de orden:
    // un vértice es UN punto y no se mueve, así que el «mínimo dos puntos» y el
    // umbral de 4 px de recorrido —que existen para que un toque al descuido no
    // siembre una figura de tamaño cero— lo descartarían siempre. Acá el toque
    // quieto es exactamente el gesto que se quiere.
    if (DE_VERTICES.includes(herramienta)) {
      setIman({ x: null, y: null });
      if (pts) ponerVertices([...vertices.current, pts[0]]);
      return;
    }

    if (!pts || pts.length < 2) return;
    const a = pts[0];
    const b = pts[pts.length - 1];

    // Un toque sin arrastrar no es una figura: 4 px de PANTALLA de umbral
    // evitan sembrar marcas de tamaño cero al tocar el plano sin querer. En el
    // trazo libre se mide el recorrido completo, para que un garabato chico
    // pero real sí cuente.
    const recorrido = DE_TRAZO.includes(herramienta)
      ? pts.reduce((s, p, i) => (i ? s + Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1]) : 0), 0)
      : Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (recorrido * vista.escala < 4) return;

    if (DE_TRAZO.includes(herramienta)) {
      // Se adelgaza en píxeles de HOJA equivalentes a 2 px de pantalla: el
      // detalle que se guarda es el que el dedo realmente podía dibujar. La
      // medida a mano alzada NO vuelve a pasar por acá: sus puntos ya nacieron
      // a paso de 8 px en `alMover`, y re-adelgazarlos ahora cambiaría el
      // número que el usuario acaba de leer en la banda.
      const paso = herramienta === 'medirLibre' ? 0 : 2 / vista.escala;
      const f = featureDeTrazo(herramienta, paso ? adelgazarTrazo(pts, paso) : pts, color, grosor);
      if (f) crearMarca(f);
      return;
    }

    if (herramienta === 'calibrar') {
      // Lo que se pide es la distancia REAL del trazo; la de píxeles ya la
      // tenemos. El cálculo lo hace el backend para que la regla viva de un
      // solo lado. Se vuelve a `mover` enseguida: calibrar es un acto, no un modo
      // en el que uno se queda.
      const px = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const resp = window.prompt(
        `¿Cuánto mide de verdad ese tramo, en metros?\n\n(el trazo midió ${px.toFixed(1)} px del plano)`,
      );
      setHerramienta('mover');
      if (resp === null) return;
      const metros = Number.parseFloat(resp.replace(',', '.'));
      if (!Number.isFinite(metros) || metros <= 0) {
        window.alert('Esa no es una distancia válida. La hoja quedó como estaba.');
        return;
      }
      onCalibrar?.(px, metros);
      return;
    }
    // Todo lo demás lo decide `marcado.ts`: acá no hay un if por herramienta.
    const f = featureDeArrastre(herramienta, a, b, color, grosor);
    if (f) crearMarca(f);
  };

  const alRueda = (ev: React.WheelEvent) => {
    ev.preventDefault();
    const r = caja.current!.getBoundingClientRect();
    // Anclado al cursor: el punto bajo el puntero no se mueve.
    zoomEnPunto(Math.exp(-ev.deltaY * 0.0015), ev.clientX - r.left, ev.clientY - r.top);
  };

  /**
   * El trazo que se está viendo AHORA: los vértices ya clavados más el punto
   * provisional que sigue al dedo. Los dos juntos y en un solo lugar, porque
   * los leen tres cosas que tienen que decir lo mismo: el dibujo, el acumulado
   * y el m² en vivo.
   *
   * Para las herramientas que no son de vértices queda igual que antes —
   * `verticesVista` está vacío y esto ES `gestoVista`—, así que calibrar y el
   * resto no cambian de comportamiento.
   */
  const trazoVivo = useMemo<[number, number][]>(
    () => [...verticesVista, ...(gestoVista ?? [])],
    [verticesVista, gestoVista],
  );

  /** El largo del trazo vivo en píxeles de hoja: la suma de TODOS sus tramos. */
  const largoVivoPx = useMemo(() => {
    let t = 0;
    for (let i = 1; i < trazoVivo.length; i++) {
      t += Math.hypot(trazoVivo[i][0] - trazoVivo[i - 1][0], trazoVivo[i][1] - trazoVivo[i - 1][1]);
    }
    return t;
  }, [trazoVivo]);

  // La suma reemplazó al viejo `hypot(primero, último)`, y no es solo por la
  // polilínea: con dos puntos las dos cuentas dan el MISMO número, así que
  // calibrar —que siempre tiene dos— siguió midiendo igual sin una rama aparte.
  const medidaEnMetros = hoja.metrosPorPixel ? largoVivoPx * hoja.metrosPorPixel : null;

  /** El m² del área en curso, para pintarlo mientras se dibuja. */
  const areaVivaEnMetros = useMemo(() => {
    if (herramienta !== 'area' || trazoVivo.length < 3 || !hoja.metrosPorPixel) return null;
    let doble = 0;
    for (let i = 0; i < trazoVivo.length; i++) {
      const [x1, y1] = trazoVivo[i];
      const [x2, y2] = trazoVivo[(i + 1) % trazoVivo.length];
      doble += x1 * y2 - x2 * y1;
    }
    return Math.abs(doble / 2) * hoja.metrosPorPixel * hoja.metrosPorPixel;
  }, [herramienta, trazoVivo, hoja.metrosPorPixel]);

  // ── La lupa de autoayuda (F-11) ───────────────────────────────────────────
  // El punto que se está colocando, ampliado en una ventana FIJA arriba a la
  // izquierda. Dos referencias y dos decisiones:
  //
  // - La lupa pegada al dedo (la de Procore) tapa justo el destino: cuando
  //   buscás una esquina, lo que necesitás ver es lo que la lupa está
  //   cubriendo. Fija en una esquina el ojo sabe siempre dónde mirar, y en la
  //   IZQUIERDA porque la mano derecha nunca cruza hasta ahí.
  // - Va sobre la fila de ver, no debajo: debajo tendría que empujar los
  //   paneles de capas/comparar cada vez que apoyás el dedo, y medir con el
  //   panel de comparación abierto es un caso normal, no raro.
  //
  // La rotación de la hoja se ignora a propósito, igual que en `aHoja`: el
  // espacio en el que se trabaja es el de la hoja sin rotar, y simularla acá
  // sola haría que la lupa mostrara otra cosa que el visor.
  // En las de vértices el punto en curso es el único que hay; en la de mano
  // alzada es el ÚLTIMO del recorrido —el que el dedo está llevando—, que es
  // lo que hay que ver ampliado mientras se bordea algo.
  const puntoEnCurso = herramienta === 'medirLibre'
    ? gestoVista?.[gestoVista.length - 1] ?? null
    : DE_VERTICES.includes(herramienta) ? gestoVista?.[0] ?? null : null;
  // Se mide contra el VISOR y no contra la ventana: con el panel de comparación
  // abierto el visor queda angosto aunque la pantalla sea grande, y ahí la lupa
  // estorba por la misma razón que en un celular.
  const lupaPx = tam.w && tam.w < VISOR_ANGOSTO ? LUPA_PX_ANGOSTA : LUPA_PX;
  const escalaLupa = vista.escala * LUPA_ZOOM;
  const vistaLupa = useMemo<Vista>(() => ({
    escala: escalaLupa,
    x: lupaPx / 2 - (puntoEnCurso?.[0] ?? 0) * escalaLupa,
    y: lupaPx / 2 - (puntoEnCurso?.[1] ?? 0) * escalaLupa,
  }), [escalaLupa, puntoEnCurso, lupaPx]);
  // Con `null` el hook no pide ni una tile: sin dedo abajo la lupa no cuesta.
  // Y pide su propio nivel de la pirámide en vez de estirar las tiles que ya
  // están en pantalla — ampliar ×3 una tile de vista completa se ve pastoso,
  // que es exactamente lo contrario de para qué sirve una lupa.
  const tilesLupa = useTiles(puntoEnCurso ? hoja : null, vistaLupa, { w: lupaPx, h: lupaPx });

  /** Los botones acercan al centro de la pantalla, no a un punto del plano. */
  const zoom = (f: number) => zoomEnPunto(f, tam.w / 2, tam.h / 2);

  // ── Editar lo seleccionado ────────────────────────────────────────────────

  const datosSel = useMemo(() => {
    const m = marcaSel !== null ? marcas.find(x => x.id === marcaSel) : undefined;
    return m ? parsearMarca(m) : null;
  }, [marcaSel, marcas]);

  // Con algo elegido, los selectores de color y grosor REPINTAN esa marca; sin
  // nada elegido, fijan con qué se va a dibujar la próxima. Es el mismo control
  // para la misma decisión, y evita una segunda fila de colores en la barra.
  const editando = datosSel !== null && !!onActualizar;
  const colorActivo = editando ? (datosSel.properties?.color || color) : color;
  const grosorActivo = editando ? (datosSel.properties?.width || grosor) : grosor;
  // Mismo criterio que el color: con algo elegido el control dice en qué capa
  // ESTÁ esa marca; sin nada elegido, en qué capa va a caer la próxima.
  const privadaActiva = editando ? privadaDe(marcaSel!) : privadaNueva;

  const aplicarColor = (c: string) => {
    if (editando) reescribirMarca(marcaSel!, reestilar(datosSel, c, grosorActivo));
    else setColor(c);
  };
  const aplicarGrosor = (g: number) => {
    if (editando) reescribirMarca(marcaSel!, reestilar(datosSel, colorActivo, g));
    else setGrosor(g);
  };
  const aplicarPrivada = (v: boolean) => {
    // La capa de destino se prende: pasar una marca a personal con esa capa
    // apagada la haría desaparecer justo al moverla, y se lee como un borrado.
    cambiarCapa(v ? 'personales' : 'publicadas', true);
    if (editando) onActualizar!(marcaSel!, datosSel, v);
    else setPrivadaNueva(v);
  };

  /** Reescribe la nota de una marca de texto. */
  const editarTexto = () => {
    if (!datosSel || !onActualizar) return;
    const texto = window.prompt('Nota sobre el plano', datosSel.properties?.description || '');
    if (texto === null) return;
    reescribirMarca(marcaSel!, {
      ...datosSel,
      properties: { ...datosSel.properties, description: texto.trim() },
    });
  };

  // ── La barra vertical ─────────────────────────────────────────────────────
  // Misma barra que el editor de la foto (F-10): «una barra, dos lugares».
  // Acá tiene además submenús por grupo, así que el componente sigue siendo
  // presentacional y la lógica del grupo vive de este lado.

  /** El grupo cuya herramienta elegida está activa — o `mover`, que no tiene grupo. */
  const grupoActivo = herramienta === 'mover'
    ? 'mover'
    : GRUPOS.find(g => elegidaPorGrupo[g.clave] === herramienta)?.clave ?? '';

  const botonesBarra = useMemo<BotonBarra<string>[]>(() => [
    { clave: 'mover', titulo: 'Mover', icono: ico(ArrowsOutCardinal) },
    ...GRUPOS.map(g => {
      const opcion = g.opciones.find(o => o.h === elegidaPorGrupo[g.clave]) || g.opciones[0];
      const sinCalibrar = g.clave === 'medir' && !hoja.metrosPorPixel;
      return {
        clave: g.clave,
        titulo: sinCalibrar ? `${opcion.nombre} (esta hoja no está calibrada)` : opcion.nombre,
        // El caret avisa que el grupo tiene hermanas. Sin él, el submenú solo
        // existe para quien lo descubra tocando dos veces.
        icono: (
          <span className="relative flex">
            {opcion.icono}
            {g.opciones.length > 1 && (
              <CaretDown size={8} weight="bold" className="absolute -bottom-1 -right-1.5 opacity-70" />
            )}
          </span>
        ),
      };
    }),
  ], [elegidaPorGrupo, hoja.metrosPorPixel]);

  const elegirDeBarra = (clave: string) => {
    setEstilos(false);
    if (clave === 'mover') {
      setHerramienta('mover'); ponerGesto(null); setSubmenu(null);
      return;
    }
    const g = GRUPOS.find(x => x.clave === clave)!;
    const activa = elegidaPorGrupo[clave];
    // Un toque elige la herramienta del grupo; si ya era la activa, abre el
    // submenú. Así volver a un grupo no obliga a pasar por el menú.
    if (herramienta === activa && g.opciones.length > 1) {
      setSubmenu(s => (s?.clave === clave ? null : { clave, top: alturaEnBarra(clave) }));
    } else {
      setHerramienta(activa); ponerGesto(null); setSubmenu(null);
    }
  };

  /** A qué altura del visor quedó el botón de un grupo, para anclarle su menú. */
  const alturaEnBarra = (clave: string) => {
    const b = raiz.current?.querySelector(`[data-barra-vertical] [data-herramienta="${clave}"]`);
    if (!b || !raiz.current) return 8;
    return b.getBoundingClientRect().top - raiz.current.getBoundingClientRect().top;
  };

  const grupoAbierto = submenu && GRUPOS.find(g => g.clave === submenu.clave);
  const conEstilo = editando || !SIN_ESTILO.includes(herramienta);

  return (
    <div ref={raiz} className="relative h-full w-full overflow-hidden rounded-lg bg-brand-gray-100">
      {/* Lo de VER el plano: zoom, encajar, capas, comparar, compartir y la
          lectura de estado. Las herramientas de DIBUJAR se fueron a la barra
          vertical de la derecha, así que acá quedó solo lo que no pinta nada.
          El `pr-14` es el ancho de esa barra: sin él, en un teléfono, la fila
          se le mete debajo y los últimos botones quedan tapados. */}
      <div className="pointer-events-none absolute inset-x-3 top-3 z-20 flex flex-col items-start gap-2 pr-14">
      <div className="pointer-events-auto flex min-w-0 flex-wrap items-center gap-1 rounded-lg bg-white/95 p-1 shadow">
        <Boton onClick={() => zoom(1 / 1.4)} titulo="Alejar"><MagnifyingGlassMinus size={16} weight="bold" /></Boton>
        <Boton onClick={() => zoom(1.4)} titulo="Acercar"><MagnifyingGlassPlus size={16} weight="bold" /></Boton>
        <Boton onClick={encajar} titulo="Encajar"><ArrowsOut size={16} weight="bold" /></Boton>
        <div className="mx-1 h-5 w-px bg-brand-gray-200" />
        {/* F-5. El punto amarillo es lo que hace honesto al filtro: una capa
            apagada hace que el plano parezca no tener esa anotación, y sin una
            señal permanente no hay forma de notar que se está mirando parcial. */}
        <button
          type="button"
          data-panel="capas"
          title={hayCapasOcultas(capas)
            ? 'Capas y filtros · hay anotaciones ocultas'
            : 'Capas y filtros'}
          onClick={() => setPanelCapas(v => !v)}
          className={`relative rounded p-1.5 transition-colors ${
            panelCapas ? 'bg-brand-green-100 text-black' : 'text-brand-gray-500 hover:bg-brand-gray-100'
          }`}
        >
          <StackSimple size={16} weight="bold" />
          {hayCapasOcultas(capas) && (
            <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-brand-yellow" />
          )}
        </button>
        {/* F-6. Comparar es un MODO: mientras está prendido el plano que se ve
            no es solo esta hoja, y el botón queda marcado para que eso no se
            confunda con un plano sucio. */}
        <button
          type="button"
          data-panel="comparar"
          title={comparando ? 'Comparando con otra hoja' : 'Comparar con otra hoja'}
          onClick={() => setPanelComparar(v => !v)}
          className={`relative rounded p-1.5 transition-colors ${
            panelComparar || comparando
              ? 'bg-brand-green-100 text-black'
              : 'text-brand-gray-500 hover:bg-brand-gray-100'
          }`}
        >
          <Intersect size={16} weight="bold" />
        </button>
        {/* F-7. Compartir NO es un modo: no cambia lo que se ve ni lo que se
            dibuja, así que el botón solo marca que el panel está abierto. */}
        <button
          type="button"
          data-panel="compartir"
          title="Compartir, imprimir y publicar"
          onClick={() => setPanelCompartir(v => !v)}
          className={`relative rounded p-1.5 transition-colors ${
            panelCompartir ? 'bg-brand-green-100 text-black' : 'text-brand-gray-500 hover:bg-brand-gray-100'
          }`}
        >
          <ShareNetwork size={16} weight="bold" />
          {/* Marcas personales sin publicar: el mismo punto amarillo que el del
              filtro, y por el mismo motivo. Una marca personal es invisible
              para el resto del equipo y nada más lo dice. */}
          {idsPersonales.length > 0 && (
            <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-brand-yellow" />
          )}
        </button>
        <div className="mx-1 h-5 w-px bg-brand-gray-200" />
        {/* La lectura de estado: el nivel y las tiles pedidas son lo que hay
            que mirar cuando el visor se sienta lento. Dejó de ser una pastilla
            aparte el día que la barra se fue a la derecha: es un número, no un
            control, y suelta solo ocupaba una esquina. La escala en metros
            vive en la banda de calibración, que es donde además se arregla. */}
        {/* `data-lectura` porque dejó de ser un <div> suelto al entrar a la
            fila: buscarlo por `div.font-mono` ya agarraba otra cosa. */}
        <span data-lectura className="px-1 font-mono text-[11px] text-brand-gray-400">
          z{nivel} · {visibles.length}
          {/* Comparando se piden las tiles de las DOS hojas: esconder la mitad
              del costo haría parecer gratis el modo que más pesa. */}
          {comparando && <>+{tilesB.visibles.length}</>} tiles
          {' '}· {Math.round(vista.escala * 100)}%
        </span>
      </div>
      {/* Los tres paneles, en un envoltorio que REACTIVA el puntero. La columna
          de arriba es `pointer-events-none` a propósito —su caja mide todo el
          ancho del visor y si cobrara clics se comería el arrastre del plano—,
          y los paneles vienen de otros archivos sin clase de puntero propia:
          sin esto se ven, se miden y no se pueden tocar. Vacío no estorba: un
          flex column sin hijos mide cero. */}
      <div className="pointer-events-auto flex flex-col items-start gap-2">
        {panelCapas && (
          <PanelCapas
            capas={capas}
            conteos={conteos}
            onCambiar={cambiarCapa}
            onVerTodo={() => setCapas(CAPAS_TODAS)}
            onCerrar={() => setPanelCapas(false)}
          />
        )}
        {panelComparar && (
          <PanelComparar
            hoja={hoja}
            hojasVersion={hojasVersion}
            idPlano={idPlano}
            idHojaB={idHojaB}
            hojaB={hojaB}
            cargandoB={cargandoB}
            modo={modoComparar}
            cual={cualHoja}
            desfase={desfase}
            pasoPx={pasoDpad}
            onElegir={elegirComparada}
            onModo={setModoComparar}
            onCual={setCualHoja}
            onMover={moverB}
            onCentrar={() => setDesfase({ x: 0, y: 0 })}
            onCerrar={() => setPanelComparar(false)}
          />
        )}
        {panelCompartir && (
          <PanelCompartir
            url={urlCompartir}
            corsTiles={corsTiles}
            personales={idsPersonales.length}
            comparando={comparando}
            publicando={publicando}
            onDescargarPng={descargarPng}
            onCopiarPng={copiarPng}
            onImprimir={imprimir}
            onPublicarPersonales={
              onPublicarPersonales && (() => onPublicarPersonales(idsPersonales))
            }
            onCerrar={() => setPanelCompartir(false)}
          />
        )}
        </div>
      </div>

      {/* La barra de herramientas, VERTICAL y pegada al borde derecho. Es el
          mismo componente que el editor de la foto (F-10): «una barra, dos
          lugares» (Daniel, 21-oct). Horizontal y arriba no servía en obra: en
          un teléfono los botones se partían en cuatro renglones y se comían el
          plano justo cuando hay que mirarlo. */}
      <BarraVertical botones={botonesBarra} activa={grupoActivo} onElegir={elegirDeBarra}>
        {/* Color, grosor y capa detrás de UN botón: con la paleta desplegada la
            barra no entra en un iPhone, y una barra que hay que scrollear para
            elegir el lápiz deja de ser una barra. Aparece solo cuando hay algo
            que estilar: mover y calibrar no pintan nada. */}
        {conEstilo && (
          <button type="button" data-estilos title="Color, grosor y capa"
            onClick={() => { setEstilos(v => !v); setSubmenu(null); }}
            className="mt-0.5 h-9 w-9 rounded-lg border-2 border-white/25"
            style={{ background: colorActivo }} />
        )}
      </BarraVertical>

      {/* El submenú del grupo. Sale A LA IZQUIERDA de la barra y a la altura de
          su propio botón: con nueve filas, un menú anclado arriba quedaría
          lejísimos del dedo que lo abrió. */}
      {grupoAbierto && (
        <div data-submenu={grupoAbierto.clave}
          className="absolute right-14 z-30 w-48 rounded-lg border border-brand-gray-200
                     bg-white py-1 shadow-lg" style={{ top: submenu?.top ?? 0 }}>
          {grupoAbierto.opciones.map(o => (
            <button
              key={o.h}
              type="button"
              // La herramienta va en el DOM porque el botón de la barra ahora
              // lleva `aria-label` con el MISMO nombre: buscar por texto da dos
              // resultados y la prueba se cae por ambigüedad.
              data-opcion={o.h}
              onClick={() => {
                setElegidaPorGrupo(p => ({ ...p, [grupoAbierto.clave]: o.h }));
                setHerramienta(o.h);
                ponerGesto(null); setSubmenu(null);
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-brand-gray-100 ${
                o.h === elegidaPorGrupo[grupoAbierto.clave] ? 'font-semibold text-black' : 'text-brand-gray-500'
              }`}
            >
              {o.icono} {o.nombre}
            </button>
          ))}
        </div>
      )}

      {/* Color, grosor y capa. Los tres se comportan igual: con una marca
          elegida la REPINTAN, sin nada elegido fijan con qué se dibuja la
          próxima. Por eso viven juntos y no en tres lugares distintos. */}
      {estilos && conEstilo && (
        <div data-panel-estilos className="absolute right-14 top-2 z-30 flex flex-col gap-2 rounded-xl
                        border border-brand-gray-200 bg-white p-2 shadow-lg">
          <div className="flex gap-1.5">
            {PALETA_COLORES.map(c => (
              <button key={c} type="button" data-color={c}
                title={editando ? `Repintar la marca de ${c}` : `Color ${c}`}
                onClick={() => aplicarColor(c)}
                className={`h-7 w-7 rounded-full ${colorActivo === c ? 'ring-2 ring-offset-1 ring-brand-gray-400' : ''}`}
                style={{ background: c }} />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {/* El grosor se muestra como lo que es: una raya de ese grosor. */}
            {GROSORES.map(gr => (
              <button key={gr.valor} type="button" data-grosor={gr.valor}
                title={`Grosor ${gr.etiqueta}`}
                onClick={() => aplicarGrosor(gr.valor)}
                className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                  grosorActivo === gr.valor ? 'bg-brand-gray-500' : 'bg-brand-gray-100'}`}>
                <span className="block w-4 rounded-full"
                  style={{
                    height: Math.min(gr.valor, 8),
                    background: grosorActivo === gr.valor ? '#fff' : '#5D636C',
                  }} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {([
              { v: false, nombre: 'publicada', detalle: 'la ve todo el equipo', icono: <UsersThree size={14} weight="bold" /> },
              { v: true, nombre: 'personal', detalle: 'solo la ves vos', icono: <User size={14} weight="bold" /> },
            ] as const).map(o => (
              <button key={String(o.v)} type="button" data-capa-destino={o.nombre}
                title={editando
                  ? `Pasar esta marca a la capa ${o.nombre} (${o.detalle})`
                  : `Dibujar en la capa ${o.nombre} (${o.detalle})`}
                onClick={() => aplicarPrivada(o.v)}
                className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 text-[11px] transition-colors ${
                  privadaActiva === o.v
                    ? 'bg-brand-gray-500 text-white'
                    : 'text-brand-gray-500 hover:bg-brand-gray-100'
                }`}
              >
                {o.icono} {o.nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Igual que arriba, todo lo de abajo vive en UN flex: antes la banda, la
          lectura de la medida, el «Guardando…» y el botón de borrar eran cuatro
          `absolute` sobre el mismo `bottom-3` y en un teléfono se montaban. */}
      <div className="pointer-events-none absolute inset-x-3 bottom-3 z-20 flex items-end justify-between gap-2">
        <div className="flex min-w-0 flex-col items-start gap-2">
          {/* Un solo número: el ACUMULADO del trazo entero, no el tramo en curso
              (pedido de Daniel, 20-oct). El área agrega su m², que es el dato
              que se está buscando; el perímetro queda como el «largo» de siempre. */}
          {trazoVivo.length >= 2
            && (DE_VERTICES.includes(herramienta) || herramienta === 'calibrar'
                || herramienta === 'medirLibre') && (
            <div className="pointer-events-auto rounded-lg bg-brand-gray-500 px-3 py-2 font-mono text-xs text-white shadow">
              {areaVivaEnMetros !== null
                ? <strong className="text-brand-green-100">{areaVivaEnMetros.toFixed(2)} m²</strong>
                : <>{largoVivoPx.toFixed(1)} px</>}
              {herramienta === 'calibrar'
                ? <> · soltá para decir cuánto mide</>
                : medidaEnMetros !== null
                  ? <> · <strong className="text-brand-green-100">{medidaEnMetros.toFixed(2)} m</strong></>
                  : null}
            </div>
          )}

          {/* Mientras hay un trazo a medio clavar, las dos salidas tienen que
              estar A LA VISTA: el doble toque cierra, pero deshacer un vértice
              no tiene gesto propio y sin botón no existiría. */}
          {DE_VERTICES.includes(herramienta) && verticesVista.length > 0 && (
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg bg-brand-gray-500 px-2 py-1.5 text-xs text-white shadow">
              <button
                type="button"
                onClick={() => ponerVertices(vertices.current.slice(0, -1))}
                className="rounded px-2 py-1 font-medium hover:bg-white/15"
              >
                Deshacer
              </button>
              <span className="text-brand-gray-300">|</span>
              <button
                type="button"
                onClick={cerrarVertices}
                disabled={verticesVista.length < minimoVertices(herramienta)}
                className="rounded bg-brand-green-100 px-2 py-1 font-semibold text-black disabled:opacity-40"
              >
                {herramienta === 'area' ? 'Cerrar área' : 'Terminar'}
              </button>
              <span className="pl-1 text-brand-gray-200">
                o doble toque
              </span>
            </div>
          )}

          {/* Qué hacer con la herramienta de vínculos. Sin esto el modo es mudo:
              se elige el icono, se toca el plano y no se entiende qué pasó. */}
          {(herramienta === 'vinculo' || herramienta === 'desvincular') && (
            <div className="pointer-events-auto max-w-xs rounded-lg bg-brand-gray-500 px-3 py-2 text-xs text-white shadow">
              {herramienta === 'vinculo'
                ? <>Tocá el punto del plano donde está el <strong>detalle</strong> y elegí a qué lámina lleva.</>
                : <>Tocá un vínculo para <strong>quitarlo</strong>. Los demás siguen abriendo.</>}
            </div>
          )}

          {/* F-8. Dice además lo que NO hace: despinchar suena a borrar la
              tarea, y la primera vez que alguien lo toque va a dudar. */}
          {(herramienta === 'chinche' || herramienta === 'despinchar') && (
            <div data-banda-chinche className="pointer-events-auto max-w-xs rounded-lg bg-brand-gray-500 px-3 py-2 text-xs text-white shadow">
              {herramienta === 'chinche'
                ? <>Tocá el punto del plano y elegí <strong>qué tarea</strong> pasa ahí. El chinche se pinta con el estado real de la tarea.</>
                : <>Tocá un chinche para <strong>sacarlo del plano</strong>. La tarea no se borra.</>}
            </div>
          )}

          {herramienta === 'foto' && (
            <div className="pointer-events-auto max-w-xs rounded-lg bg-brand-gray-500 px-3 py-2 text-xs text-white shadow">
              Tocá el punto del plano donde <strong>sacaste la foto</strong> y elegí
              el archivo. Queda clavada ahí aunque entre una versión nueva.
            </div>
          )}

          {/* F-6. La misma clase de aviso que el del filtro, y por la misma
              razón: comparando, lo que se ve NO es esta hoja. Sin decirlo, un
              plano teñido de rojo y azul se lee como un plano roto. Además es
              el único lugar donde se aclara de quién son las marcas que siguen
              pintadas: las de la hoja BASE, no las de la comparada. */}
          {comparando && hojaB && (
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-brand-gray-300 bg-white/95 px-2.5 py-1.5 shadow">
              <span className="flex items-center gap-1 text-[11px] text-brand-gray-500">
                {modoComparar === 'super' ? (
                  <>
                    <Cuadrito color={COLOR_A} /> v{hoja.version}
                    <span className="text-brand-gray-400">contra</span>
                    <Cuadrito color={COLOR_B} /> v{hojaB.version}
                  </>
                ) : (
                  <>Viendo <strong>v{cualHoja === 'a' ? hoja.version : hojaB.version}</strong> de las dos comparadas</>
                )}
              </span>
              <span className="text-[11px] text-brand-gray-400">· marcas de v{hoja.version}</span>
              <button type="button" data-dejar-comparar onClick={dejarDeComparar}
                className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-brand-gray-500 hover:bg-brand-gray-100">
                Dejar de comparar
              </button>
            </div>
          )}

          {/* El aviso de que se está mirando el plano FILTRADO. Es el mismo
              criterio que la banda de «sin calibrar»: el estado se ve y se
              arregla en el mismo lugar. Sin esto, una capa apagada hace que el
              plano parezca no tener esas anotaciones y nadie lo nota — es el
              modo de falla propio de un filtro, no un lujo. */}
          {hayCapasOcultas(capas) && (
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-brand-yellow bg-white/95 px-2.5 py-1.5 shadow">
              <EyeSlash size={13} weight="bold" className="shrink-0 text-brand-gray-400" />
              <span className="text-[11px] text-brand-gray-500">
                Estás viendo el plano <strong>filtrado</strong>.
              </span>
              <button type="button" onClick={() => setCapas(CAPAS_TODAS)}
                className="rounded bg-brand-green-100 px-2 py-0.5 text-[11px] font-semibold text-black hover:bg-brand-green-200">
                Ver todo
              </button>
            </div>
          )}

          {/* La banda de calibración. Es UNA y siempre está, calibrada o no:
              antes el único rastro de la calibración era la palabra «sin
              calibrar» repetida sobre cada medida, y no había forma de fijarla
              ni de revisarla («el tema de calibrar nunca he entendido cómo
              poder hacerlo», 16-oct-2026). Acá se ve el estado y se actúa en el
              mismo lugar. */}
          {onCalibrar && (herramienta === 'calibrar' ? (
            <div className="pointer-events-auto max-w-xs rounded-lg bg-brand-gray-500 px-3 py-2 text-xs text-white shadow">
              Marcá sobre el plano una distancia que <strong>ya conozcas</strong> (un
              lindero, una cota) y después escribí cuánto mide.
            </div>
          ) : hoja.metrosPorPixel ? (
            /* La PROCEDENCIA de la medida, no solo la medida. Un `1 px = 0,423
               cm` a secas no distingue lo que leyó una máquina del cajetín de
               lo que midió una persona con el dedo, y la diferencia importa:
               el rótulo dice lo que el dibujante TECLEÓ. Si la lámina se
               imprimió con «scaled to fit» o le cambiaron la escala al dibujo
               sin tocar el rótulo, el número sale confiado y falso.

               Citar el «1:50» es lo que vuelve la nota accionable: quien
               conoce la lámina ve de un golpe si esa escala es la que
               esperaba, y «Recalibrar» ya está al lado. Por eso la nota no
               lleva un «revise si ve una anomalía» escrito: la escala a la
               vista ES la invitación a revisarla, y un cartel fijo que se lee
               veinte veces al día deja de leerse.

               Con `origenCalibracion` en null la banda queda EXACTAMENTE como
               estaba: son las hojas calibradas antes de que el dato existiera
               y no sabemos de dónde salió su medida (ver la migración 031).
               Inventarles una procedencia sería peor que no mostrar ninguna. */
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg bg-white/95 px-2.5 py-1.5 shadow">
              {hoja.origenCalibracion === 'rotulo' && (
                <span className="flex items-center gap-1 text-[11px] text-brand-gray-500">
                  <MagicWand size={13} weight="bold" className="shrink-0 text-brand-green-200" />
                  {/* La coma va PEGADA a la palabra en la misma línea: cortar
                      el JSX antes de ella le mete un espacio y sale
                      «Calibrado solo , del rótulo». */}
                  <span>
                    Calibrado solo{hoja.escalaCajetin ? ', del rótulo: ' : ''}
                    {hoja.escalaCajetin && <strong>1:{hoja.escalaCajetin}</strong>}
                  </span>
                </span>
              )}
              {hoja.origenCalibracion === 'manual' && (
                <span className="flex items-center gap-1 text-[11px] text-brand-gray-500">
                  <Hand size={13} weight="bold" className="shrink-0 text-brand-gray-400" />
                  Calibrado a mano
                </span>
              )}
              {/* El punto medio separa la nota del dato, que es el separador
                  que ya usa la banda del acumulado al medir. Sin él los dos
                  textos se leen como una sola frase corrida. */}
              {hoja.origenCalibracion && <span className="text-brand-gray-300">·</span>}
              <span className="font-mono text-[11px] text-brand-gray-400">
                1 px = {(hoja.metrosPorPixel * 100).toFixed(3)} cm
              </span>
              <button type="button" onClick={() => { setHerramienta('calibrar'); ponerGesto(null); }}
                className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-brand-gray-500 hover:bg-brand-gray-100">
                Recalibrar
              </button>
            </div>
          ) : (
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-brand-yellow bg-white/95 px-2.5 py-1.5 shadow">
              <span className="text-[11px] text-brand-gray-500">
                Hoja <strong>sin calibrar</strong>: se puede anotar, pero no medir.
              </span>
              <button type="button" onClick={() => { setHerramienta('calibrar'); ponerGesto(null); }}
                className="rounded bg-brand-green-100 px-2 py-0.5 text-[11px] font-semibold text-black hover:bg-brand-green-200">
                Calibrar
              </button>
            </div>
          ))}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          {guardando && (
            <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-brand-gray-500 px-3 py-1.5 text-xs text-white shadow">
              <CircleNotch size={13} weight="bold" className="animate-spin" /> Guardando…
            </div>
          )}

          {/* Borrar la marca seleccionada. Aparece solo con algo elegido: un
              botón de borrar siempre visible en un plano lleno de anotaciones es
              un accidente esperando. */}
          {marcaSel !== null && (
            <div className="pointer-events-auto flex items-center gap-2 rounded-lg bg-white/95 px-2 py-1.5 shadow">
              <span className="font-mono text-[11px] text-brand-gray-400">marca #{marcaSel}</span>
              {/* El color y el grosor de la marca elegida se editan ARRIBA, en
                  la misma fila con la que se elige con qué dibujar. Acá quedan
                  las dos acciones que no son estilo. */}
              {datosSel?.properties?.style === 'text' && onActualizar && (
                <button
                  type="button"
                  onClick={editarTexto}
                  className="flex items-center gap-1 rounded border border-brand-gray-300 px-2 py-1 text-xs font-semibold text-brand-gray-500 hover:bg-brand-gray-100"
                >
                  <TextT size={13} weight="bold" /> Texto
                </button>
              )}
              {onBorrar && (
                <button
                  type="button"
                  onClick={() => { onBorrar(marcaSel); setMarcaSel(null); }}
                  className="flex items-center gap-1 rounded border border-brand-red-100 px-2 py-1 text-xs font-semibold text-brand-red-200 hover:bg-brand-red-100/10"
                >
                  <Trash size={13} weight="bold" /> Borrar
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* La lupa. `pointer-events-none` no es un detalle de estilo: la mano
          puede pasar por encima mientras mide, y si cobrara el evento el trazo
          se cortaría solo al cruzarla. */}
      {puntoEnCurso && (
        <div
          data-lupa
          className="pointer-events-none absolute left-3 top-3 z-30 overflow-hidden rounded-full bg-white shadow-lg"
          style={{ width: lupaPx, height: lupaPx, border: `2px solid ${COLOR_MEDIDA}` }}
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: hoja.anchoPx,
              height: hoja.altoPx,
              transform: `translate(${vistaLupa.x}px, ${vistaLupa.y}px) scale(${escalaLupa})`,
            }}
          >
            <CapaTiles hoja={hoja} nivel={tilesLupa.nivel} visibles={tilesLupa.visibles} />
            {/* El trazo que viene llegando, para poder cerrar un área sobre su
                primer vértice sin salir de la lupa. Las marcas guardadas NO se
                replican: la lupa es un instrumento de puntería, y meterle todo
                el plano la volvería la misma vista pero chica. */}
            {trazoVivo.length > 1 && (
              <svg viewBox={`0 0 ${hoja.anchoPx} ${hoja.altoPx}`} width={hoja.anchoPx} height={hoja.altoPx}
                className="absolute left-0 top-0">
                <polyline
                  points={trazoVivo.map(([x, y]) => `${x},${y}`).join(' ')}
                  fill="none" stroke={COLOR_MEDIDA} strokeWidth={2}
                  strokeLinejoin="round" strokeLinecap="round"
                  vectorEffect="non-scaling-stroke" />
                {verticesVista.map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r={4 / escalaLupa}
                    fill="#fff" stroke={COLOR_MEDIDA} strokeWidth={2}
                    vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
            )}
          </div>
          {/* La mira va en píxeles del CÍRCULO y no de la hoja: el punto está
              siempre en el centro por construcción, así que dibujarla acá la
              deja inmune al zoom y a la hoja. El hueco del medio es lo que
              hace servir la lupa — una cruz entera taparía el píxel que se
              está tratando de ver. */}
          {/* Los brazos y el hueco van en píxeles fijos, no en proporción del
              círculo: el hueco existe para no tapar el píxel que se está
              mirando, y ese píxel no se achica porque la lupa se achique. */}
          <svg width={lupaPx} height={lupaPx} className="absolute left-0 top-0">
            {[[-20, -6], [6, 20]].map(([a, b], i) => (
              <g key={i} stroke={COLOR_MEDIDA} strokeWidth={1.5}>
                <line x1={lupaPx / 2 + a} y1={lupaPx / 2} x2={lupaPx / 2 + b} y2={lupaPx / 2} />
                <line x1={lupaPx / 2} y1={lupaPx / 2 + a} x2={lupaPx / 2} y2={lupaPx / 2 + b} />
              </g>
            ))}
            {/* El acuse del imán, el mismo que en el plano: sin esto no hay
                forma de saber desde la lupa si el punto se alineó. */}
            {(iman.x != null || iman.y != null) && (
              <circle cx={lupaPx / 2} cy={lupaPx / 2} r={4} fill={COLOR_MEDIDA} />
            )}
          </svg>
        </div>
      )}

      <div
        ref={caja}
        className="h-full w-full select-none touch-none"
        style={{ cursor: herramienta !== 'mover' ? 'crosshair' : arrastre.current ? 'grabbing' : 'grab' }}
        onPointerDown={alBajar}
        onPointerMove={alMover}
        onPointerUp={alSubir}
        onPointerCancel={alSubir}
        onWheel={alRueda}
      >
        <div
          className="absolute left-0 top-0 origin-top-left bg-white shadow-lg"
          style={{
            width: hoja.anchoPx,
            height: hoja.altoPx,
            // La rotación va DENTRO del mismo transform (y al final, para que
            // gire la hoja y no el encuadre): el plano suele venir acostado
            // respecto al papel.
            transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.escala})`
              + (hoja.rotacionGrados ? ` rotate(${hoja.rotacionGrados}deg)` : ''),
          }}
        >
          {/* La hoja BASE. Sin comparar va pelada, exactamente como antes. El
              tinte y el aislamiento aparecen solo en modo comparación, y el
              `bg-white` del grupo no es decorativo: `screen` sobre un fondo
              transparente devuelve el tinte a full y pintaría la hoja entera de
              rojo. Sobre blanco no hace nada, que es lo que hay que lograr
              para que el papel siga siendo papel. */}
          {!(comparando && modoComparar === 'alternar' && cualHoja === 'b') && (
            <div
              data-hoja="a"
              className="absolute inset-0 bg-white"
              style={comparando && modoComparar === 'super' ? { isolation: 'isolate' } : undefined}
            >
              <CapaTiles hoja={hoja} nivel={nivel} visibles={visibles} />
              {comparando && modoComparar === 'super' && (
                <div className="pointer-events-none absolute inset-0"
                  style={{ mixBlendMode: 'screen', background: COLOR_A }} />
              )}
            </div>
          )}

          {/* La hoja COMPARADA, en el mismo espacio de píxeles y corrida por el
              desfase del D-pad. `multiply` contra la base es lo que hace la
              lectura diferencial: línea en las dos = casi negro, solo en la
              base = rojo, solo en la comparada = azul. `isolation` evita que su
              propio tinte se componga contra la base en vez de contra ella. */}
          {comparando && hojaB && !(modoComparar === 'alternar' && cualHoja === 'a') && (
            <div
              data-hoja="b"
              className="absolute left-0 top-0 bg-white"
              style={{
                width: hojaB.anchoPx,
                height: hojaB.altoPx,
                transform: `translate(${desfase.x}px, ${desfase.y}px) scale(${escalaB})`,
                transformOrigin: 'top left',
                isolation: 'isolate',
                ...(modoComparar === 'super' ? { mixBlendMode: 'multiply' as const } : {}),
              }}
            >
              <CapaTiles hoja={hojaB} nivel={tilesB.nivel} visibles={tilesB.visibles} />
              {modoComparar === 'super' && (
                <div className="pointer-events-none absolute inset-0"
                  style={{ mixBlendMode: 'screen', background: COLOR_B }} />
              )}
            </div>
          )}

          {/* Lo que entra acá es la lista YA FILTRADA por capas: una marca
              escondida no se pinta y por lo tanto tampoco se puede agarrar ni
              arrastrar sin verla, que es lo que pasaría si el filtro fuera solo
              un `opacity: 0`. */}
          <CapaMarcas
            anchoPx={hoja.anchoPx}
            altoPx={hoja.altoPx}
            marcas={marcasVisibles}
            escala={vista.escala}
            metrosPorPixel={hoja.metrosPorPixel}
            idSeleccionada={marcaSel}
            onAgarrar={herramienta === 'mover' ? agarrarMarca : undefined}
            desplazada={movidaVista}
          />

          {/* F-9. El trazo de vértices. Va aparte del gesto de abajo porque dura
              MÁS que un toque: sigue en pantalla con el dedo levantado, y por
              eso no puede colgar de `gestoVista`. */}
          {DE_VERTICES.includes(herramienta) && trazoVivo.length > 0 && (() => {
            // Todo lo que se mide con el ojo —la mira, el radio del vértice, el
            // punteado— va en píxeles de PANTALLA: en metros crecería con el
            // zoom y a ×7 la mira taparía media hoja.
            const pp = (px: number) => px / vista.escala;
            const prov = gestoVista?.[0] ?? null;
            const comoD = (pts: [number, number][]) => pts.map(([x, y]) => `${x},${y}`).join(' ');
            const traza = {
              stroke: COLOR_MEDIDA,
              strokeWidth: grosor,
              vectorEffect: 'non-scaling-stroke' as const,
              strokeLinejoin: 'round' as const,
              strokeLinecap: 'round' as const,
            };
            return (
              <svg viewBox={`0 0 ${hoja.anchoPx} ${hoja.altoPx}`} width={hoja.anchoPx} height={hoja.altoPx}
                className="pointer-events-none absolute left-0 top-0">
                {/* Las guías del imán: cruzan la hoja entera para que se vea
                    CONTRA QUÉ vértice se está alineando, no solo que agarró. */}
                {iman.x != null && (
                  <line x1={iman.x} y1={0} x2={iman.x} y2={hoja.altoPx}
                    stroke={COLOR_MEDIDA} strokeWidth={1} strokeDasharray={`${pp(18)} ${pp(11)}`}
                    vectorEffect="non-scaling-stroke" />
                )}
                {iman.y != null && (
                  <line x1={0} y1={iman.y} x2={hoja.anchoPx} y2={iman.y}
                    stroke={COLOR_MEDIDA} strokeWidth={1} strokeDasharray={`${pp(18)} ${pp(11)}`}
                    vectorEffect="non-scaling-stroke" />
                )}

                {/* El área se previsualiza rellena: lo que importa es la
                    superficie que se está encerrando, no la línea. */}
                {herramienta === 'area' && trazoVivo.length > 2 && (
                  <polygon points={comoD(trazoVivo)} {...traza} fill={COLOR_MEDIDA} fillOpacity={0.18} />
                )}
                {verticesVista.length > 1 && (
                  <polyline points={comoD(verticesVista)} {...traza} fill="none" />
                )}
                {/* El tramo que todavía no se clavó va punteado: es la promesa,
                    no el trazo. */}
                {prov && verticesVista.length > 0 && (
                  <line
                    x1={verticesVista[verticesVista.length - 1][0]}
                    y1={verticesVista[verticesVista.length - 1][1]}
                    x2={prov[0]} y2={prov[1]}
                    {...traza} strokeDasharray={`${pp(6)} ${pp(5)}`} />
                )}
                {verticesVista.map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r={pp(4)}
                    fill="#fff" stroke={COLOR_MEDIDA} strokeWidth={2}
                    vectorEffect="non-scaling-stroke" />
                ))}
                {/* La mira, con el punto macizo solo cuando el imán agarró:
                    es el único acuse de recibo de que el vértice se alineó. */}
                {prov && (
                  <g>
                    <circle cx={prov[0]} cy={prov[1]} r={pp(14)}
                      fill="none" stroke={COLOR_MEDIDA} strokeWidth={3}
                      vectorEffect="non-scaling-stroke" />
                    {(iman.x != null || iman.y != null) && (
                      <circle cx={prov[0]} cy={prov[1]} r={pp(7)} fill={COLOR_MEDIDA} />
                    )}
                    <line x1={prov[0] - pp(9)} y1={prov[1]} x2={prov[0] + pp(9)} y2={prov[1]}
                      stroke={COLOR_MEDIDA} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                    <line x1={prov[0]} y1={prov[1] - pp(9)} x2={prov[0]} y2={prov[1] + pp(9)}
                      stroke={COLOR_MEDIDA} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                  </g>
                )}
              </svg>
            );
          })()}

          {/* El trazo en curso, en el mismo espacio de píxeles que todo lo demás.
              Se pinta con el color elegido para que lo que se ve mientras se
              arrastra sea lo que va a quedar. */}
          {gestoVista && !DE_VERTICES.includes(herramienta) && (
            <svg viewBox={`0 0 ${hoja.anchoPx} ${hoja.altoPx}`} width={hoja.anchoPx} height={hoja.altoPx}
              className="pointer-events-none absolute left-0 top-0">
              {(() => {
                const comun = {
                  // Medir se ve en fucsia aunque se guarde con el color
                  // elegido, igual que las de vértices: mientras se traza, lo
                  // que está en pantalla es el instrumento, no la marca.
                  stroke: herramienta === 'medirLibre' ? COLOR_MEDIDA : color,
                  strokeWidth: grosor,
                  vectorEffect: 'non-scaling-stroke' as const,
                  strokeLinejoin: 'round' as const,
                  strokeLinecap: 'round' as const,
                  ...(herramienta === 'marcador' ? { strokeOpacity: 0.35, strokeWidth: Math.max(grosor * 4, 18) } : {}),
                };
                const comoD = (pts: [number, number][]) => pts.map(([x, y]) => `${x},${y}`).join(' ');

                // El lazo de área se fue con F-9: acá ya solo quedan trazos
                // abiertos (lápiz y marcador).
                if (DE_TRAZO.includes(herramienta)) {
                  return <polyline points={comoD(gestoVista)} {...comun} fill="none" />;
                }
                const a = gestoVista[0];
                const b = gestoVista[gestoVista.length - 1];
                if (herramienta === 'rect') {
                  return <rect
                    x={Math.min(a[0], b[0])} y={Math.min(a[1], b[1])}
                    width={Math.abs(b[0] - a[0])} height={Math.abs(b[1] - a[1])}
                    {...comun} fill={color} fillOpacity={0.2} />;
                }
                if (herramienta === 'oval') {
                  return <polygon points={comoD(puntosOvalo(a, b))} {...comun} fill={color} fillOpacity={0.2} />;
                }
                if (herramienta === 'nube') {
                  return <polygon points={comoD(puntosNube(a, b))} {...comun} fill="none" />;
                }
                return <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} {...comun} />;
              })()}
            </svg>
          )}

          {/* Lo clavado al plano. Los chinches y las fotos pertenecen al PLANO
              (no se borran al subir una versión nueva); los vínculos, a la HOJA. */}
          {capas.chinches && <CapaChinches
            chinches={chinches}
            escala={vista.escala}
            // F-8: el chinche es un botón recién ahora. En modo «despinchar» el
            // clic BORRA en vez de abrir — el mismo chinche, dos modos, igual
            // que el vínculo.
            onAbrirTarea={herramienta === 'despinchar' ? undefined : onAbrirTarea}
            onBorrar={herramienta === 'despinchar' ? onBorrarChinche : undefined}
          />}

          {capas.fotos && <CapaFotos fotos={fotos} escala={vista.escala} onVer={setFotoVista} />}

          {capas.vinculos && <CapaVinculos
            vinculos={vinculos}
            escala={vista.escala}
            onIr={onIrALamina}
            // Con la herramienta de desvincular, tocar un vínculo lo borra en vez
            // de saltar: el mismo chinche, dos modos. Un botón de borrar siempre
            // visible sobre cada vínculo sería un accidente esperando.
            onBorrar={herramienta === 'desvincular' ? onBorrarVinculo : undefined}
          />}
        </div>
      </div>

      {/* LA FOTO SE CLAVA TOCANDO UN `<label>` DE VERDAD, no simulando un clic.
          (Daniel, 20-oct-2026: «le doy clic al botón de fotos, pero a la hora de
          hacerle tap en cualquier parte del plano no pasa nada».)

          El diseño anterior era: `alBajar` guardaba el punto y llamaba a
          `selectorFoto.current.click()`. Eso funciona en una compu y NO funciona
          en el iPhone, porque Safari de iOS solo le abre el selector de archivos
          a un gesto que nace de un `click` o un `touchend` real. Un `.click()`
          disparado desde `pointerdown` (= `touchstart`) lo descarta en silencio:
          ni abre nada ni tira error. Medirlo en Chrome no sirve de nada —ahí el
          `.click()` desde `pointerdown` SÍ abre el selector—, así que la prueba
          verde de `foto-camara.py` nunca lo iba a ver.

          Por eso no hay más clic por código. Mientras la herramienta está activa,
          una capa transparente tapa el plano y ESA capa es un `<label>` con el
          input adentro: el dedo toca el label, el navegador abre el selector por
          su cuenta y no hay activación de usuario que perder. El punto se captura
          en el `pointerdown` del propio label, que corre antes.

          Se renderiza SOLO en modo foto: una capa permanente se comería el pan,
          el pellizco y el dibujo de todas las demás herramientas.

          `z-10` la pone sobre la hoja y bajo las dos barras (`z-20`), así la
          banda de instrucciones y el botón de Calibrar siguen siendo tocables.

          Dos detalles del input, que son del celular y solo del celular:
          1. `sr-only` y no `hidden`: `display:none` deja a algunos Safari sin
             abrir el selector ni siquiera desde el label.
          2. `accept="image/*"` y no la lista `jpeg,png,webp`. El iPhone guarda en
             HEIC, y una lista explícita le deja las fotos grises en el carrete
             —se abre el selector y no se puede elegir nada—. Con `image/*` Safari
             transcodifica a JPEG al entregar el archivo, que es justo lo que el
             backend acepta (valida por bytes mágicos: JPG/PNG/WEBP).
          Sin `capture`, para que el sistema ofrezca la cámara Y la galería:
          forzando la cámara no se podría clavar una foto ya tomada. */}
      {onSubirFoto && herramienta === 'foto' && (
        <label
          data-capa-foto
          className="absolute inset-0 z-10 cursor-crosshair"
          onPointerDown={ev => {
            const [x, y] = aHoja(ev);
            puntoFoto.current = { posX: x, posY: y };
          }}
        >
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={ev => {
            const archivo = ev.target.files?.[0];
            if (archivo && puntoFoto.current) onSubirFoto(puntoFoto.current, archivo);
            puntoFoto.current = null;
            // Se limpia o elegir DOS VECES el mismo archivo no dispara `change`
            // la segunda: el valor no cambió y el navegador no avisa.
            ev.target.value = '';
            // Clavar una foto es un acto, no un modo: igual que el vínculo.
            setHerramienta('mover');
          }}
        />
        </label>
      )}

      {/* La foto grande, también fuera del transformado. Desde F-10 ya no es un
          visor: se dibuja encima. Va fuera del `transform` justamente por eso —
          adentro heredaría el zoom del plano y el trazo habría salido a otra
          escala que el dedo. */}
      {fotoVista && (
        <EditorFoto
          foto={fotoVista}
          onCerrar={() => setFotoVista(null)}
          onBorrar={onBorrarFoto && (id => { onBorrarFoto(id); setFotoVista(null); })}
          onGuardar={onGuardarMarcasFoto}
          guardando={guardandoMarcasFoto}
        />
      )}

      {/* El diálogo del vínculo va FUERA del div transformado: adentro heredaría
          el `scale` de la vista y a la escala de «encajar» saldría ilegible. */}
      {vinculoNuevo && idPlano !== undefined && (
        <DialogoVinculo
          idPlanoActual={idPlano}
          onCancelar={() => setVinculoNuevo(null)}
          onConfirmar={(planoDestinoId, descripcion) => {
            onCrearVinculo?.({ posX: vinculoNuevo.x, posY: vinculoNuevo.y, planoDestinoId, descripcion });
            setVinculoNuevo(null);
            // Se vuelve a mover: vincular es un acto, no un modo en el que uno
            // se queda. Mismo criterio que calibrar.
            setHerramienta('mover');
          }}
        />
      )}

      {/* F-8, fuera del transformado por la misma razón que el del vínculo. */}
      {chincheNuevo && (
        <DialogoChinche
          onCancelar={() => setChincheNuevo(null)}
          onConfirmar={(tareaId, titulo) => {
            onCrearChinche?.({ posX: chincheNuevo.x, posY: chincheNuevo.y, tareaId, titulo });
            setChincheNuevo(null);
            // Vuelve a «mover» para que el chinche recién clavado se pueda
            // TOCAR de una: quedándose en modo chinche, el clic encima abriría
            // otro diálogo en vez de abrir la tarea.
            setHerramienta('mover');
          }}
        />
      )}
    </div>
  );
}

/** El cuadrito de color de la leyenda de comparación. */
function Cuadrito({ color }: { color: string }) {
  return <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: color }} />;
}

/** Acción suelta de la fila de arriba. Ya no lleva estado activo: lo único que
 *  se elige —la herramienta— se fue a la barra vertical, y zoom y encajar son
 *  actos, no modos. */
function Boton({ children, onClick, titulo }: {
  children: React.ReactNode; onClick: () => void; titulo: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      className="rounded p-1.5 text-brand-gray-500 transition-colors hover:bg-brand-gray-100"
    >
      {children}
    </button>
  );
}
